import { BybButton } from "@Components/common/byb-ds";
import BybVrDialog from "@Components/game/common/dialog/byb-vr-dialog";
import useI18n from "@i18n";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import StarIcon from "@Resources/app/icons-v2/system-icons/star-icon";
import { MAX_SCAN_RATE_FOR_BYB_IQ } from "@Shared/utilities/scanScoreUtilities";

interface IScanRateScoreMaxedDialog {
  open: boolean;
  onClose: () => void;
}

const ScanRateScoreMaxedDialog = ({ open, onClose }: IScanRateScoreMaxedDialog) => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const maxScanRate = MAX_SCAN_RATE_FOR_BYB_IQ.toFixed(1);
  const horizontalPadding = isMdDown ? 3 : 6;

  const starColor = theme.palette.dsPurple?.v200 ?? theme.palette.dsPrimary.v200;

  return (
    <BybVrDialog
      name="scan-rate-score-maxed"
      open={open}
      onClose={onClose}
      showCloseButton
      width={isMdDown ? "90vw" : "30vw"}
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <Stack
        spacing={2}
        alignItems="center"
        textAlign="center"
        padding={2}
        sx={{
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            backgroundColor: `${starColor}15`,
          }}
        >
          <StarIcon color={starColor} size={24} />
        </Stack>

        <Box
          sx={{
            width: "100%",
            boxSizing: "border-box",
            padding: theme.spacing(0, horizontalPadding),
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxHeight: "calc(90vh - 220px)",
              overflowY: "auto",
            }}
          >
            <Stack spacing={2} width="100%">
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Typography variant="text3xl700" color="dsBase.v200">
                  {i18n("session-results.scan-rate-maxed.title")}
                </Typography>

                <Typography variant="textBase500" color="dsNeutral.v300" paddingTop={theme.spacing(2)}>
                  {i18n("session-results.scan-rate-maxed.intro", { maxScanRate })}
                </Typography>
              </Stack>

              <Stack spacing={0.75} alignItems="flex-start" textAlign="left" width="100%" paddingTop={theme.spacing(4)}>
                <Typography variant="textBase700" color="dsBase.v200">
                  {i18n("session-results.scan-rate-maxed.why.title")}
                </Typography>
                <Typography variant="textBase500" color="dsNeutral.v300">
                  {i18n("session-results.scan-rate-maxed.why.body", { maxScanRate })}
                </Typography>
              </Stack>

              <Stack spacing={0.75} alignItems="flex-start" textAlign="left" width="100%" paddingTop={theme.spacing(4)}>
                <Typography variant="textBase700" color="dsBase.v200">
                  {i18n("session-results.scan-rate-maxed.whats-next.title")}
                </Typography>
                <Typography variant="textBase500" color="dsNeutral.v300">
                  {i18n("session-results.scan-rate-maxed.whats-next.body")}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          <Box width="100%" paddingTop={theme.spacing(6)} paddingBottom={theme.spacing(2)}>
            <BybButton variant="contained" fullWidth onClick={onClose} sx={{ borderRadius: 500, paddingY: 1.5, textTransform: "none" }}>
              <Typography variant="textBase600">{i18n("button.close")}</Typography>
            </BybButton>
          </Box>
        </Box>
      </Stack>
    </BybVrDialog>
  );
};

export default ScanRateScoreMaxedDialog;
